import type { PayrollBatchRow } from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import { formatDate } from '@/utils';
import { Flex } from 'antd';
import type { ReactNode } from 'react';
import { formatBDT, toAmount } from '../shared/payroll.helpers';
import { dailyPayOf } from './batch-payroll.helpers';

type Props = {
  open: boolean;
  row?: PayrollBatchRow | null;
  onClose: () => void;
};

type Entry = [label: string, value: unknown];

/**
 * Breakdown keys that are not money. Daily rows put their cycle facts into the
 * structure breakdown, so they are formatted here instead of shown as BDT.
 */
const NON_MONEY: Partial<Record<string, (value: unknown) => string>> = {
  cycle_no: (value) => `Cycle ${value}`,
  total_days: (value) => `${value} days`,
  present_days: (value) => `${value} days`,
  payable_days: (value) => `${value} days`,
  cycle_start_date: (value) => formatDate(value as string, 'DD MMM YYYY'),
  cycle_end_date: (value) => formatDate(value as string, 'DD MMM YYYY'),
};

/** `{ house_rent: 500 }` → `[['house_rent', 500]]`, nothing for an empty object. */
const toEntries = (data?: object | null): Entry[] =>
  Object.entries(data || {}).filter(([key]) => key !== 'inactive_status_time');

const DAILY_PAY_FIELDS = new Set([
  'gross',
  'amount',
  'cycle_no',
  'daily_rate',
  'total_days',
  'present_days',
  'payable_days',
  'cycle_start_date',
  'cycle_end_date',
]);

/**
 * Read-only view of how one employee's final salary was reached: structure,
 * grade benefits, additions, bonuses, deductions and tax.
 */
const SalaryBreakdownDrawer = ({ open, row, onClose }: Props) => {
  const { pro_rata: proRata, ...salaryComponents } =
    row?.base_salary_structure_breakdown || {};
  const dailyPay = row ? dailyPayOf(row) : null;
  const hasBaseSalary =
    row?.total_base_salary_structure_amount != null &&
    row.total_base_salary_structure_amount !== '' &&
    !dailyPay;
  const adjustedBaseSalary = hasBaseSalary ? (
    <Flex
      justify="space-between"
      align="center"
      gap={16}
      className="rounded-xl border border-theme-primary/30 bg-theme-primary/5 p-4"
    >
      <span className="text-sm font-medium text-white">
        Payable Base Salary
      </span>
      <span className="shrink-0 text-lg font-bold tracking-tight text-theme-primary tabular-nums">
        {formatBDT(row.total_base_salary_structure_amount)}
      </span>
    </Flex>
  ) : null;
  const baseSalarySummary = proRata?.is_partial ? (
    <div className="mt-8 rounded-xl border border-theme-gray-700/60 p-4">
      <h5 className="mb-4 text-sm font-semibold text-white">
        Pro-rata calculation
      </h5>
      <span className="inline-flex rounded-full bg-theme-primary/15 px-3 py-1 text-xs font-semibold text-theme-primary">
        Partial
      </span>
      <Flex
        justify="space-between"
        align="center"
        gap={16}
        className="mb-6 mt-3 text-sm"
      >
        <span className="text-white/80">Payable days</span>
        <span className="font-semibold text-white tabular-nums">
          {proRata.worked_days} of {proRata.total_days} days
        </span>
      </Flex>
      {adjustedBaseSalary}
    </div>
  ) : adjustedBaseSalary ? (
    <div className="mt-8">{adjustedBaseSalary}</div>
  ) : null;

  // Tax arrives as its own field but comes off the final salary, so it is
  // listed with the other deductions.
  const hasTax = row?.tax_amount != null && row.tax_amount !== '';
  const deductions: Entry[] = [
    ...toEntries(row?.fixed_deduction),
    ...(hasTax ? ([['tax', row?.tax_amount]] as Entry[]) : []),
  ];

  const sections: {
    title: string;
    entries: Entry[];
    isDeduction?: boolean;
    summary?: ReactNode;
  }[] =
    [
      {
        title: 'Daily Pay',
        entries: [],
        summary:
          dailyPay && row ? (
            <DailyPaySummary
              row={row}
              payableDays={dailyPay.payableDays}
              dailyRate={dailyPay.dailyRate}
            />
          ) : null,
      },
      {
        title: 'Base Salary Structure',
        entries: toEntries(salaryComponents).filter(
          ([key]) => !dailyPay || !DAILY_PAY_FIELDS.has(key),
        ),
        summary: baseSalarySummary,
      },
      {
        title: 'Grade Benefits',
        entries: toEntries(row?.grade_benefits_breakdown),
      },
      { title: 'Other Additions', entries: toEntries(row?.fixed_addition) },
      {
        title: 'Bonus Additions',
        entries: (row?.bonus_addition || []).map((bonus): Entry => [
          bonus.title,
          bonus.amount,
        ]),
      },
      { title: 'Deductions', entries: deductions, isDeduction: true },
    ].filter((section) => section.entries.length > 0 || section.summary);

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title="Salary Breakdown"
      width={550}
    >
      <div className="py-2 pr-2">
        {sections.map((section) => (
          <BreakdownSection key={section.title} {...section} />
        ))}

        {!sections.length && (
          <div className="text-center py-10 text-white/50 italic">
            No detailed breakdown available for this record.
          </div>
        )}

        {row?.adjustment_note && (
          <div className="mb-8 rounded-xl border border-theme-gray-700 bg-theme-slate-900/40 p-4">
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">
              Adjustment Note
            </div>
            <p className="text-white/90 text-sm">{row.adjustment_note}</p>
          </div>
        )}

        {row?.net_salary != null && row.net_salary !== '' && (
          <div className="mt-6 pt-6 border-t border-theme-gray-700">
            <Flex
              justify="space-between"
              align="center"
              gap={16}
              className="px-6 py-5 rounded-2xl bg-gradient-to-r from-theme-primary/20 via-theme-primary/5 to-transparent border border-theme-primary/30"
            >
              <div className="flex flex-col">
                <span className="text-white/50 text-xs font-black uppercase tracking-widest mb-1">
                  Net Payable Total
                </span>
                <span className="text-white text-xl font-bold">
                  Final Salary
                </span>
              </div>
              <span className="shrink-0 text-theme-primary text-3xl font-black tracking-tighter tabular-nums">
                {formatBDT(row.net_salary)}
              </span>
            </Flex>
          </div>
        )}
      </div>
    </CustomDrawer>
  );
};

/** Daily cycle facts and earnings, grouped separately from salary components. */
const DailyPaySummary = ({
  row,
  payableDays,
  dailyRate,
}: {
  row: PayrollBatchRow;
  payableDays: number;
  dailyRate: number;
}) => {
  const breakdown = row.base_salary_structure_breakdown;
  const cycleNo = toAmount(breakdown?.cycle_no);
  const totalDays =
    breakdown?.total_days != null ? toAmount(breakdown.total_days) : null;
  const startDate = breakdown?.cycle_start_date;
  const endDate = breakdown?.cycle_end_date;
  const hasStartDate = typeof startDate === 'string' && startDate !== '';
  const hasEndDate = typeof endDate === 'string' && endDate !== '';
  const gross =
    row.daily_pay_breakdown?.amount ??
    breakdown?.gross ??
    row.total_base_salary_structure_amount;

  return (
    <div className="overflow-hidden rounded-2xl border border-theme-gray-700/60 bg-theme-slate-900/40">
      <div className="p-5">
        {(cycleNo > 0 || hasStartDate || hasEndDate) && (
          <div className="mb-5 border-b border-theme-gray-700/40 pb-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-widest text-white/50">
                Payroll period
              </span>
              {cycleNo > 0 && (
                <span className="rounded-full border border-theme-primary/20 bg-theme-primary/10 px-3 py-1 text-xs font-semibold text-theme-primary">
                  Cycle {cycleNo}
                </span>
              )}
            </div>
            {(hasStartDate || hasEndDate) && (
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-medium text-white">
                {hasStartDate && (
                  <time dateTime={startDate}>
                    {formatDate(startDate, 'DD MMM YYYY')}
                  </time>
                )}
                {hasStartDate && hasEndDate && (
                  <span className="text-theme-primary/70">
                    <span aria-hidden="true">&rarr;</span>
                    <span className="sr-only">to</span>
                  </span>
                )}
                {hasEndDate && (
                  <time dateTime={endDate}>
                    {formatDate(endDate, 'DD MMM YYYY')}
                  </time>
                )}
              </div>
            )}
          </div>
        )}

        <dl className="grid grid-cols-2 gap-3">
          <div className="min-w-0 rounded-xl border border-theme-gray-700/30 bg-white/[0.02] p-4">
            <dt className="mb-2 text-xs text-white/50">Payable days</dt>
            <dd className="flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
              <span className="text-2xl font-bold leading-none text-white">
                {payableDays}
              </span>
              <span className="text-xs text-white/50">
                {totalDays != null ? `of ${totalDays} days` : 'days'}
              </span>
            </dd>
          </div>
          <div className="min-w-0 rounded-xl border border-theme-gray-700/30 bg-white/[0.02] p-4">
            <dt className="mb-2 text-xs text-white/50">Daily rate</dt>
            <dd className="break-words text-lg font-semibold leading-none text-white tabular-nums">
              {formatBDT(dailyRate)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-theme-primary/20 bg-theme-primary/5 px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-white">Gross Pay</p>
          <p className="mt-1 text-xs text-white/50">
            {payableDays} {payableDays === 1 ? 'day' : 'days'} &times;{' '}
            {formatBDT(dailyRate)}
          </p>
        </div>
        <span className="break-all text-2xl font-bold tracking-tight text-theme-primary tabular-nums">
          {formatBDT(gross)}
        </span>
      </div>
    </div>
  );
};

/** One titled group of label / amount lines. */
const BreakdownSection = ({
  title,
  entries,
  isDeduction = false,
  summary,
}: {
  title: string;
  entries: Entry[];
  isDeduction?: boolean;
  summary?: ReactNode;
}) => (
  <div className="mb-8 last:mb-0">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-px flex-1 bg-gradient-to-r from-theme-primary/50 to-transparent" />
      <h4 className="text-white/70 text-[11px] font-black uppercase tracking-[0.2em]">
        {title}
      </h4>
      <div className="h-px flex-1 bg-gradient-to-l from-theme-primary/50 to-transparent" />
    </div>

    <div className="grid grid-cols-1 gap-3">
      {entries.map(([key, value], index) => {
        const asText = NON_MONEY[key];
        const amount = toAmount(value);
        return (
          <Flex
            key={`${key}-${index}`}
            justify="space-between"
            align="center"
            gap={16}
            className="bg-theme-slate-900/40 p-4 rounded-xl border border-theme-gray-700/30 hover:border-theme-primary/20 transition-colors"
          >
            <span className="text-white/90 text-sm font-medium capitalize">
              {key.replace(/_/g, ' ')}
            </span>
            <span
              className={`shrink-0 text-lg font-bold tracking-tight tabular-nums ${
                asText
                  ? 'text-white/80'
                  : isDeduction
                    ? 'text-theme-error-500'
                    : 'text-theme-primary'
              }`}
            >
              {asText
                ? asText(value)
                : `${isDeduction && amount > 0 ? '-' : ''}${formatBDT(amount)}`}
            </span>
          </Flex>
        );
      })}
    </div>
    {summary}
  </div>
);

export default SalaryBreakdownDrawer;
